import { useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';

import type { KbFilters } from '@/api/kb-client';
import { KbAdminScreen } from '@/routes/admin/communication/kb-admin-screen';

// =============================================================================
// kb-admin-route.tsx — Adaptateur routeur KB ADMIN « Base de connaissance » (R15-COM-04).
// =============================================================================
// L'ÉTAT DE VUE (recherche `q`, campagne, SIRET, période `from`/`to`, fil ouvert `thread`) est porté par
// l'URL via TanStack Router : partageable, compatible bouton retour, aucun état métier en localStorage
// (RULE_REF_006). Le composant de présentation (KbAdminScreen) reste testable hors routeur (props d'état).

/** Forme validée du search de la route KB admin (état d'URL). */
export interface KbAdminSearch {
  readonly q?: string;
  readonly campaign?: string;
  readonly siret?: string;
  readonly from?: string;
  readonly to?: string;
  readonly thread?: string;
}

/** Valide/normalise le search (allowlist stricte, défauts non sérialisés → URL propre). */
export function validateKbAdminSearch(
  search: Record<string, unknown>,
): KbAdminSearch {
  const result: {
    q?: string;
    campaign?: string;
    siret?: string;
    from?: string;
    to?: string;
    thread?: string;
  } = {};
  const q = search['q'];
  if (typeof q === 'string' && q.length > 0) {
    result.q = q;
  }
  const campaign = search['campaign'];
  if (typeof campaign === 'string' && campaign.length > 0) {
    result.campaign = campaign;
  }
  const siret = search['siret'];
  if (typeof siret === 'string' && siret.length > 0) {
    result.siret = siret;
  }
  const from = search['from'];
  if (typeof from === 'string' && from.length > 0) {
    result.from = from;
  }
  const to = search['to'];
  if (typeof to === 'string' && to.length > 0) {
    result.to = to;
  }
  const thread = search['thread'];
  if (typeof thread === 'string' && thread.length > 0) {
    result.thread = thread;
  }
  return result;
}

/** Projette le search d'URL en filtres client (clés absentes = pas de filtre). */
function toFilters(search: KbAdminSearch): KbFilters {
  return {
    ...(search.q !== undefined ? { q: search.q } : {}),
    ...(search.campaign !== undefined ? { campaignId: search.campaign } : {}),
    ...(search.siret !== undefined ? { supplierSiret: search.siret } : {}),
    ...(search.from !== undefined ? { archivedFrom: search.from } : {}),
    ...(search.to !== undefined ? { archivedTo: search.to } : {}),
  };
}

export function KbAdminRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as KbAdminSearch;

  const filters = toFilters(search);
  const selectedThreadId = search.thread ?? null;

  const onFiltersChange = React.useCallback(
    (next: KbFilters): void => {
      void navigate({
        to: '/admin/knowledge-base',
        search: {
          ...(next.q !== undefined ? { q: next.q } : {}),
          ...(next.campaignId !== undefined ? { campaign: next.campaignId } : {}),
          ...(next.supplierSiret !== undefined
            ? { siret: next.supplierSiret }
            : {}),
          ...(next.archivedFrom !== undefined ? { from: next.archivedFrom } : {}),
          ...(next.archivedTo !== undefined ? { to: next.archivedTo } : {}),
          // Le fil ouvert survit au changement de filtre : on le conserve.
          ...(selectedThreadId !== null ? { thread: selectedThreadId } : {}),
        },
      });
    },
    [navigate, selectedThreadId],
  );

  const onSelectThread = React.useCallback(
    (threadId: string | null): void => {
      void navigate({
        to: '/admin/knowledge-base',
        search: {
          ...(search.q !== undefined ? { q: search.q } : {}),
          ...(search.campaign !== undefined ? { campaign: search.campaign } : {}),
          ...(search.siret !== undefined ? { siret: search.siret } : {}),
          ...(search.from !== undefined ? { from: search.from } : {}),
          ...(search.to !== undefined ? { to: search.to } : {}),
          ...(threadId !== null ? { thread: threadId } : {}),
        },
      });
    },
    [navigate, search.q, search.campaign, search.siret, search.from, search.to],
  );

  return (
    <KbAdminScreen
      filters={filters}
      onFiltersChange={onFiltersChange}
      selectedThreadId={selectedThreadId}
      onSelectThread={onSelectThread}
    />
  );
}
